import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Users,
  Target,
  Zap,
  Shield,
  GraduationCap
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Services() {

  const navigate = useNavigate();

  const services = [
    {
      icon: Briefcase,
      title: "Talent Acquisition",
      description:
        "Strategic recruitment solutions to identify and attract top-tier talent aligned with your organizational culture and objectives.",
      color: "text-blue-600"
    },
    {
      icon: Users,
      title: "Recruitment Process Outsourcing",
      description:
        "End-to-end recruitment management that reduces time-to-hire and improves candidate quality while optimizing costs.",
      color: "text-purple-600"
    },
    {
      icon: Target,
      title: "HR Consulting",
      description:
        "Expert guidance on organizational strategy, compensation design, and HR infrastructure to maximize employee engagement.",
      color: "text-amber-500"
    },
    {
      icon: Zap,
      title: "Payroll Management",
      description:
        "Accurate, compliant payroll processing with expertise in statutory compliance across multiple jurisdictions.",
      color: "text-green-600"
    },
    {
      icon: GraduationCap,
      title: "Employee Training",
      description:
        "Customized development programs that enhance skills, boost retention, and create a culture of continuous learning.",
      color: "text-pink-600"
    },
    {
      icon: Shield,
      title: "HR Compliance",
      description:
        "Comprehensive compliance management ensuring adherence to labor laws and regulatory requirements.",
      color: "text-indigo-600"
    }
  ];


  return (

    <section className="bg-white py-24 px-6">

      <div className="max-w-7xl mx-auto">


        {/* Section Heading */}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >

          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-black via-gray-700 to-black bg-clip-text text-transparent">
            Our Services
          </h2>

          <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto"></div>

          <p className="text-gray-600 mt-6 max-w-2xl mx-auto text-lg">
            Comprehensive HR solutions designed to help organizations build
            stronger teams, improve leadership, and drive sustainable growth.
          </p>

        </motion.div>



        {/* Services Grid */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

          {services.map((service, i) => {

            const Icon = service.icon;

            return (

              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}

                className="p-8 bg-white border border-gray-200 shadow-sm hover:shadow-lg transition"
              >

                <Icon
                  size={40}
                  className={`${service.color} mb-6`}
                />

                <h3 className="text-xl font-semibold text-black mb-3">
                  {service.title}
                </h3>

                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>

              </motion.div>

            );

          })}

        </div>



        {/* Bottom CTA */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mt-24"
        >

          <h3 className="text-2xl font-bold mb-4 text-black">
            Looking for a tailored HR solution?
          </h3>

          <p className="text-gray-600 mb-8">
            Our consultants are ready to help your organization design the
            right workforce strategy.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/contact")}
            className="bg-black text-white px-8 py-3 font-semibold hover:bg-gray-900 transition"
          >
            Schedule Consultation
          </motion.button>


        </motion.div>


      </div>

    </section>

  );
}